<i18n>
[
    "recentlyViewed__tray--title",
]
</i18n>

<template>
    <div v-if="showRecentlyViewedTray" :style="style">
        <ProductTray
            :id="id"
            :listId="listId"
            :listName="analyticsListName"
            :contentModuleId="contentModuleId"
            :contentTitle="contentTitle"
            :products="products"
            :headerTitle="t('recentlyViewed__tray--title')"
            :totalProducts="totalProducts"
            :mobilePeek="mobilePeek"
            :mobilePeekPercent="mobilePeekPercent"
            :mediumPeekPercent="mediumPeekPercent"
            :isFullBleedDesktop="true"
            :displayCounter="displayCounter"
            :desktopSlideSize="desktopSlideSize"
            :xlDesktopSlideSize="xlDesktopSlideSize"
            :mediumSlideSize="mediumSlideSize"
            :mobileSlideSize="mobileSlideSize"
            :translucentButtons="translucentButtons"
            :lightButtons="lightButtons"
            :lightInfoContainer="lightInfoContainer"
            :catalogResponseId="catalogResponseId"
            :showProductInfo="showProductInfo"
            :analyticsType="analyticsType"
            :traceId="traceId"
            :faceOutImage="faceOutImage"
            :hasEnteredViewport="hasEnteredViewport"
            :isLoaded="isLoaded"
            :fillEmptyPageWithBlankTiles="false"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useR15Svc } from '~coreModules/core/js/composables/useR15Svc';
import { useLogger } from '~coreModules/core/js/composables/useLogger';

// NU_FEATURE: recentlyViewedTray
// https://urbnit.atlassian.net/browse/TYP-35217
import runtimeConfig from '~config/config';

import { CAROUSEL_IDS, PRODUCT_LIST_NAMES } from '~modules/analytics/js/analytics-constants';
import { getRecentlyViewedItems } from '~routes/browse/js/browse-api';

import productTrayProps from '~coreModules/core/js/ui/productTrayProps';

import ProductTray from '~modules/core/components/ui/ProductTray.vue';

const props = defineProps({
    ...productTrayProps,
    categoryQuery: {
        type: Object,
        default: null,
    },
    maxProducts: {
        type: Number,
        default: 100,
    },
    shouldShowHeaderLink: {
        type: Boolean,
        default: true,
    },
    lightInfoContainer: {
        type: Boolean,
        default: true,
    },
    applySizePreferencesAndAvailability: {
        type: Boolean,
        default: false,
    },
    paddingBottom: {
        type: String,
        default: null,
    },
    traceId: {
        type: String,
        default: null,
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
});

const { t } = useI18n();
const r15Svc = useR15Svc();
const logger = useLogger();

const products = ref([]);
// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const totalProducts = ref(props.maxProducts);
const catalogResponseId = ref(null);
const isLoaded = ref(false);

const analyticsType = CAROUSEL_IDS.RECENTLY_VIEWED_TRAY;
const analyticsListName = PRODUCT_LIST_NAMES.RECENTLY_VIEWED_TRAY;

// NU_FEATURE: recentlyViewedTray
// https://urbnit.atlassian.net/browse/TYP-35217
const showRecentlyViewedTray = computed(() => runtimeConfig.features.recentlyViewedTray &&
    (!isLoaded.value || (isLoaded.value && products.value.length >= 2)));

const style = computed(() => {
    if (props.paddingBottom) {
        return {
            paddingBottom: props.paddingBottom,
        };
    }

    return {};
});

async function getRecentlyViewedProducts() {
    try {
        const { products: returnedProducts, responseId } = await getRecentlyViewedItems(r15Svc);
        catalogResponseId.value = responseId;
        totalProducts.value = returnedProducts.length;
        products.value = returnedProducts;
    } catch (error) {
        logger.debugError('Failed to fetch recently viewed items: ', error);
        Promise.reject(error);
    } finally {
        isLoaded.value = true;
    }
}

watch(() => props.hasEnteredViewport, async (newValue, oldValue) => {
    if (!oldValue && newValue && showRecentlyViewedTray.value) {
        await getRecentlyViewedProducts();
    }
}, { immediate: true });
</script>

<style lang="scss">

</style>
